import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Radio, Row } from 'antd';
import { CallDataRecordsLast5, CardLocationGoogleMap, Catcher, cardLayoutOptions } from '../';
import { IGetApiType } from '../../REST';
import { IShowFormComponent } from '../../interfaces';
import {
    useCardLocationList,
    useIsGeolocationEnabled,
    useProfile,
    useProfileFeatureFlags,
    useProfileIsRight,
    useRouterStore,
} from '../../hooks';
import {
    abortSelectedFetch,
    cleanCallDataRecordsStore,
    cleanCardStore,
    cleanCatalogsStore,
    getCard,
    getCardShowRelationSelect,
    getCardStatistics,
} from '../../middleware';
import { CardInfoUsage, DeviceHistoryTable, EditCardInfo, PackageUsage, ShowCardInfo, Subscribers } from './Components';
import Styles from '../styles/m_itemCardStyles.less';

export const Card: React.FC<IShowFormComponent> = observer(({ isEdit }: IShowFormComponent) => {
    const featureFlags = useProfileFeatureFlags();
    const routerStore = useRouterStore();
    const [currentView, setCurrentView] = useState<'dataUsage' | 'top5' | 'last5' | 'packageUsage'>('dataUsage');
    const isGeolocationEnabled = useIsGeolocationEnabled();
    const cardLocationList = useCardLocationList();
    const [id, setId] = useState('');
    const isLord = useProfileIsRight('LORD');
    const isGeolocationShown = isGeolocationEnabled && cardLocationList.length && isLord;
    const { isCompanyTypeIdMNOHost, companyId } = useProfile();
    const idSpinnersMain: IGetApiType[] = ['GET_CARD'];
    const forStartEditApiTypes: IGetApiType[] = [
        ...idSpinnersMain,
        'GET_CARD_SETS_SELECT',
        'GET_SUBSCRIBERS_BY_CARD_ID',
    ];
    const usedApiTypes: IGetApiType[] = [
        ...forStartEditApiTypes,
        'GET_CALL_DATA_RECORDS_LAST_5',
        'GET_SUBSCRIBERS_STATUS',
    ];

    const goOut = (): void => {
        abortSelectedFetch(usedApiTypes);
        cleanCatalogsStore();
        cleanCardStore();
        cleanCallDataRecordsStore();
    };

    const goToList = (): void => {
        routerStore.goTo('CardList');
        goOut();
    };

    const _getCard = async (id: string): Promise<void> => {
        if (!!id) {
            getCardShowRelationSelect(id);
            const isGetCard = await getCard(id);

            if (isGetCard) {
                await getCardStatistics(id, companyId);
            } else {
                goToList();
            }
        }
    };

    useEffect(() => {
        return (): void => {
            goOut();
        };
    }, []);

    useEffect(() => {
        if (id) {
            _getCard(id);
        }
    }, [id]);

    useEffect(() => {
        if (
            routerStore.routerState.params.id &&
            (routerStore.routerState.routeName === 'Card' || routerStore.routerState.routeName === 'CardEdit')
        ) {
            setId(routerStore.routerState.params.id);
        }
    }, [routerStore.routerState.params.id]);

    const setEdit = (isEdit?: boolean): void => {
        if (isEdit) {
            routerStore.goTo('CardEdit', { params: { id } });
        } else {
            routerStore.goBack();
        }
    };

    const currentComponent = {
        subs: <Subscribers />,
        dataUsage: <CardInfoUsage />,
        top5:
            !isEdit && isLord ? (
                <DeviceHistoryTable
                    columnOption={cardLayoutOptions.topFiveUsedDevices}
                    idSpinners={idSpinnersMain}
                    title={'Top 5 used devices'}
                />
            ) : null,
        last5:
            !isEdit && !isCompanyTypeIdMNOHost ? (
                <CallDataRecordsLast5
                    columnOption={cardLayoutOptions.cardTableFull}
                    idSpinners={['GET_CALL_DATA_RECORDS_LIST', 'GET_CALL_DATA_RECORDS_LAST_5']}
                />
            ) : null,
        packageUsage: <PackageUsage cardId={id} />,
    };

    return (
        <Catcher>
            <article className={Styles.main}>
                <Row>
                    {isEdit ? (
                        <EditCardInfo setEditMode={setEdit} />
                    ) : (
                        <ShowCardInfo setEditMode={setEdit} loadingTypes={forStartEditApiTypes} />
                    )}
                    <Subscribers />
                    {featureFlags.createTariffs ? (
                        <>
                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1,
                                    flexDirection: 'column',
                                    width: '100%',
                                    marginTop: 20,
                                }}
                            >
                                <div>
                                    <Radio.Group
                                        style={{ padding: '0 20px' }}
                                        defaultValue="dataUsage"
                                        buttonStyle="solid"
                                        onChange={(e) => setCurrentView(e.target.value)}
                                    >
                                        <Radio.Button value="packageUsage">Package Usage</Radio.Button>
                                        <Radio.Button value="dataUsage">Card Data Usage </Radio.Button>
                                        {!isEdit && isLord ? (
                                            <Radio.Button value="top5">Top 5 Devices</Radio.Button>
                                        ) : null}
                                        <Radio.Button value="last5">Last 5 CDRS</Radio.Button>
                                    </Radio.Group>
                                </div>

                                <div>{currentComponent[currentView]}</div>
                            </div>
                            {!isEdit && isGeolocationShown ? (
                                <CardLocationGoogleMap
                                    columnOption={cardLayoutOptions.cardTableFull}
                                    idSpinners={idSpinnersMain}
                                    title={'Device location'}
                                />
                            ) : null}
                        </>
                    ) : (
                        <>
                            <CardInfoUsage />
                            {!isEdit && isLord ? (
                                <DeviceHistoryTable
                                    columnOption={cardLayoutOptions.topFiveUsedDevices}
                                    idSpinners={idSpinnersMain}
                                    title={'Top 5 used devices'}
                                />
                            ) : null}
                            {!isEdit && isGeolocationShown ? (
                                <CardLocationGoogleMap
                                    columnOption={cardLayoutOptions.cardTableFull}
                                    idSpinners={idSpinnersMain}
                                    title={'Device location'}
                                />
                            ) : null}
                            {!isEdit && !isCompanyTypeIdMNOHost ? (
                                <CallDataRecordsLast5
                                    columnOption={cardLayoutOptions.cardTableFull}
                                    idSpinners={['GET_CALL_DATA_RECORDS_LIST', 'GET_CALL_DATA_RECORDS_LAST_5']}
                                />
                            ) : null}
                        </>
                    )}
                </Row>
            </article>
        </Catcher>
    );
});
